import { CurrencyPipe } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { ProductPriceData } from "@domain/product/product-price-data";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject } from "@domain/project/fgds-project";
import { getRequiredFgdsProjectServicesWithPositions } from "@domain/project/fgds-service-definition";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { MonitoringGroupSection } from "@domain/project/monitoring-product-list/monitoring-group-section";
import { MonitoringProductListService } from "@domain/project/monitoring-product-list/monitoring-product-list.service";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { GroupSection } from "@domain/project/product-list/group-section";
import { calculateDiscountedPrice, getProductListPriceData } from "@domain/project/product-list/product-list-discount.util";
import {
  buildFgdsAdditionalCategoryDisplayData,
  buildFgdsPlacedGroupDisplayData,
  buildMonitoringAdditionalCategoryDisplayData,
  buildMonitoringPlacedGroupDisplayData,
  FgdsSectionDisplayData,
  isAttachmentInstructionsDisplayData,
  MonitoringSectionDisplayData,
  ProductListAttachmentInstructionsDisplayData,
  ProductListGroupDisplayData,
} from "@domain/project/product-list/product-list-display-data.util";
import { comparePositionThenName } from "@domain/project/product-list/product-list-position.util";
import { AdditionalProductConfigSection, ProductListService } from "@domain/project/product-list/product-list.service";
import { ProjectCostService } from "@domain/project/product-list/project-cost.service";
import { UserService } from "@domain/user/user.service";
import { ProductListServiceRow } from "@pdf/export-services/product-information-pages/product-list-pages/product-list-service-row";
import { ProductTableRow } from "@pdf/export-services/product-information-pages/product-list-pages/product-table-row";
import { ServicesTable } from "@pdf/export-services/product-information-pages/product-list-pages/services-table";
import { TableOfContents } from "@pdf/export-services/table-of-contents/table-of-contents";
import { HalfPageContainer } from "@pdf/helpers/container/half-page/half-page-container";
import { HalfPageContainerService } from "@pdf/helpers/container/half-page/half-page-container.service";
import { TableColumn } from "@pdf/helpers/default-table/table-column";
import { MAX_HEADLINE_WIDTH, truncateTextToWidth } from "@pdf/helpers/pdf-text.helper";
import {
  ContainerProperties,
  FontNames,
  Headlines,
  ImagePaths,
  PdfProperties,
  ProductListProperties,
  TableProperties,
} from "@pdf/pdf-properties";
import { CountryCurrencyMapper } from "@utils/country-currency-mapper";
import { jsPDF } from "jspdf";
import { first, forkJoin, map, Observable, of, switchMap } from "rxjs";
import { ProductListPlaceholderRow } from "./product-list-placeholder-row";
import { ProductListProductRow } from "./product-list-product-row";
import { ProductTable } from "./product-table";

interface ServicesTableRow {
  position: number;
  title: string;
  description: string;
  id: string;
  costs: number;
}

interface ServiceData {
  position: number;
  notes: string;
  id: string;
  title: string;
  costs?: number;
}

@Injectable({
  providedIn: "root",
})
export class ProductListPdfService {
  private containerService = inject(HalfPageContainerService);
  private userService = inject(UserService);
  private productService = inject(ProductService);
  private monitoringProductListService = inject(MonitoringProductListService);
  private productListService = inject(ProductListService);
  private projectCostService = inject(ProjectCostService);

  private headlines = new Headlines();
  private countryMapping = new CountryCurrencyMapper();
  private isFirstContainerDrawn = false;
  private localeId!: string;
  private currencyPipe: CurrencyPipe;
  private currentShowZrefPrices = false;
  private static readonly ZREF_CURRENCY = "EUR";

  constructor() {
    this.userService.user$.pipe(first()).subscribe((user) => {
      this.localeId = user.language;
    });
    this.currencyPipe = new CurrencyPipe(this.localeId);
  }

  public generateMonitoringProductList(
    pdf: jsPDF,
    project: MonitoringProject,
    floorplans: Floorplan[],
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    isProductListOverall = false,
  ) {
    this.isFirstContainerDrawn = false;
    this.initPdfStyle(pdf);
    return forkJoin([
      this.initMonitoringGroupSections(project, floorplans),
      isProductListOverall ? this.monitoringProductListService.createAdditionalConfigSections(project) : of([]),
    ]).pipe(
      map(([createdGroupSections, additionalSections]) => {
        const placedGroups = buildMonitoringPlacedGroupDisplayData(createdGroupSections ?? []);
        if (placedGroups.length > 0) {
          this.drawStandaloneSectionHeadline(
            pdf,
            container,
            tableOfContents,
            $localize`:@@productList.productsOnFloorPlan:Produkte auf dem Grundrissplan`,
            undefined,
            false,
            isProductListOverall ? undefined : placedGroups[0]?.source.floorplanName,
            true,
          );
        }
        placedGroups.forEach((group) => {
          this.drawMonitoringPlacedGroup(pdf, container, tableOfContents, false, group, isProductListOverall);
        });
        const additionalCategories = buildMonitoringAdditionalCategoryDisplayData(false, additionalSections, {});
        if (isProductListOverall && additionalCategories.length > 0) {
          this.drawStandaloneSectionHeadline(
            pdf,
            container,
            tableOfContents,
            this.headlines.ADDITIONAL_PRODUCTS,
            undefined,
            false,
            undefined,
            true,
          );
          this.addAdditionalMonitoringProductTables(pdf, container, tableOfContents, false, additionalCategories);
        }
        if (this.isFirstContainerDrawn) {
          this.addFooter(pdf, container);
        }
      }),
    );
  }

  public generateFgdsProductList(
    pdf: jsPDF,
    project: FgdsProject,
    floorplans: Floorplan[],
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    shouldIncludeServices: boolean,
    isProductListOverall = false,
  ): Observable<void> {
    const showCosts = project.showCostFlag;
    this.isFirstContainerDrawn = false;
    this.initPdfStyle(pdf);

    return this.userService.user$.pipe(
      first(),
      switchMap((user) => {
        const showZrefPrices = user.showZrefPrices ?? false;
        this.currentShowZrefPrices = showZrefPrices;
        return forkJoin([
          this.initFgdsGroupSections(project, floorplans),
          isProductListOverall ? this.productListService.createAdditionalConfigSections(project) : of([]),
        ]).pipe(
          map(([createdGroupSections, additionalSections]) => {
            const groupSections = createdGroupSections ?? [];
            const placedGroups = buildFgdsPlacedGroupDisplayData(
              groupSections,
              (group) => this.productListService.getAttachmentInstructionsForGroup(group),
              (instructions) => this.projectCostService.calculateTotalAttachmentInstructionsPrice(instructions, showZrefPrices),
            )
              .map((group) => ({
                ...group,
                sections: group.sections.filter((section) => !isAttachmentInstructionsDisplayData(section)),
              }))
              .filter((group) => group.sections.length > 0);
            const additionalCategories = buildFgdsAdditionalCategoryDisplayData(false, additionalSections, {});
            const rootAttachmentInstructions = this.buildRootAttachmentInstructions(groupSections, additionalSections);
            const allProductsSubtotal = showZrefPrices
              ? groupSections.reduce((t, g) => t + g.zrefSubTotal, 0) +
                additionalCategories.reduce((t, c) => t + (c.zrefSubTotal ?? 0), 0) +
                this.projectCostService.calculateTotalAttachmentInstructionsPrice(rootAttachmentInstructions, true)
              : this.projectCostService.calculateTotalProductPriceForGroupedExport(groupSections, additionalSections);
            const placedProductsSubtotal = groupSections.reduce(
              (total, g) => total + (showZrefPrices ? g.zrefSubTotal : g.subTotal),
              0,
            );
            const additionalProductsSubtotal = additionalCategories.reduce(
              (total, c) => total + (showZrefPrices ? (c.zrefSubTotal ?? 0) : (c.subtotal ?? 0)),
              0,
            );

            if (placedGroups.length > 0) {
              this.drawStandaloneSectionHeadline(
                pdf,
                container,
                tableOfContents,
                $localize`:@@productList.productsOnFloorPlan:Produkte auf dem Grundrissplan`,
                placedProductsSubtotal,
                showCosts,
                isProductListOverall ? undefined : placedGroups[0]?.source.floorplanName,
                true,
              );
            }
            placedGroups.forEach((group) => {
              this.drawFgdsPlacedGroup(pdf, container, tableOfContents, showCosts, group, isProductListOverall, showZrefPrices);
            });
            if (isProductListOverall && additionalCategories.length > 0) {
              this.drawStandaloneSectionHeadline(
                pdf,
                container,
                tableOfContents,
                this.headlines.ADDITIONAL_PRODUCTS,
                additionalProductsSubtotal,
                showCosts,
                undefined,
                true,
              );
              this.addAdditionalFgdsProductTables(
                pdf,
                container,
                tableOfContents,
                showCosts,
                additionalCategories,
                showZrefPrices,
              );
            }
            if (rootAttachmentInstructions.length > 0) {
              const attachmentInstructionsTable = new ProductTable(
                pdf,
                this.localeId,
                this.headlines.ATTACHMENT_INSTRUCTIONS,
                this.createAttachmentInstructionRows(rootAttachmentInstructions, showZrefPrices),
                showCosts,
                this.projectCostService.calculateTotalAttachmentInstructionsPrice(rootAttachmentInstructions, showZrefPrices),
              );
              this.drawProductTable(
                pdf,
                container,
                tableOfContents,
                attachmentInstructionsTable,
                showCosts,
                isProductListOverall ? undefined : groupSections[0]?.floorplanName,
                true,
              );
            }
            const latestProductPositionId = this.productListService.getLatestPositionIdForProducts(
              groupSections,
              additionalSections,
            );
            if (shouldIncludeServices) {
              const servicesCost: number = this.addServicesTable(
                project,
                pdf,
                container,
                tableOfContents,
                this.headlines.SERVICES,
                latestProductPositionId,
              );
              if (this.isFirstContainerDrawn && showCosts) {
                this.drawProductListOutro(pdf, container, tableOfContents, servicesCost, allProductsSubtotal);
              } else if (this.isFirstContainerDrawn) {
                container.yPosition -= TableProperties.GAP_BETWEEN_TABLES;
                this.addFooter(pdf, container);
              }
            } else {
              if (this.isFirstContainerDrawn && showCosts) {
                this.drawFloorplanProductListOutro(
                  pdf,
                  container,
                  tableOfContents,
                  createdGroupSections[0]?.floorplanName ?? "",
                  allProductsSubtotal,
                );
              } else if (this.isFirstContainerDrawn) {
                this.addFooter(pdf, container);
              }
            }
          }),
        );
      }),
    );
  }

  private initMonitoringGroupSections(project: MonitoringProject, floorplans: Floorplan[]): Observable<MonitoringGroupSection[]> {
    return this.monitoringProductListService.createGroupSections(project, floorplans);
  }

  private initFgdsGroupSections(project: FgdsProject, floorplans: Floorplan[]): Observable<GroupSection[]> {
    return this.productListService.createGroupSectionsForProject(project, floorplans);
  }

  private initPdfStyle(pdf: jsPDF) {
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    pdf.setLineWidth(TableProperties.SEPARATOR_WIDTH);
    pdf.setDrawColor(TableProperties.SEPARATOR_COLOR);
  }

  private drawProductListOutro(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    servicesCost: number,
    overallProductCost: number,
  ) {
    if (!this.checkIfIsEnoughSpace(ProductListProperties.FULL_SUMMARY_HEIGHT, container)) {
      this.addFooter(pdf, container);
      this.containerService.add(pdf, container, this.headlines.PRODUCT_LIST_OVERALL, tableOfContents);
    }
    this.drawSummary(pdf, container, servicesCost, overallProductCost);
    this.drawSummaryFooter(pdf, container);
  }

  private drawFloorplanProductListOutro(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    floorplanName: string,
    overallProductCost?: number,
  ) {
    if (!this.checkIfIsEnoughSpace(ProductListProperties.SUMMARY_HEIGHT, container)) {
      this.addFooter(pdf, container);
      this.containerService.add(pdf, container, this.buildPageHeadline(pdf, floorplanName), tableOfContents);
    }
    this.drawFloorplanSummary(pdf, container, overallProductCost ?? 0);
    this.drawFloorplanSummaryFooter(pdf, container);
  }

  private drawMonitoringPlacedGroup(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    showCosts: boolean,
    group: ProductListGroupDisplayData<MonitoringSectionDisplayData, never, MonitoringGroupSection>,
    isProductListOverall = false,
  ) {
    group.sections.forEach((section) => {
      const table = new ProductTable(
        pdf,
        this.localeId,
        `${group.label} - ${section.label}`,
        this.createMonitoringProductListRows(section.section),
        showCosts,
      );
      this.drawProductTable(
        pdf,
        container,
        tableOfContents,
        table,
        showCosts,
        isProductListOverall ? undefined : group.source.floorplanName,
      );
    });
  }
  private drawFgdsPlacedGroup(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    showCosts: boolean,
    group: ProductListGroupDisplayData<FgdsSectionDisplayData, ProductListAttachmentInstructionsDisplayData, GroupSection>,
    isProductListOverall = false,
    showZrefPrices = false,
  ) {
    group.sections.forEach((section) => {
      const subtotal = isAttachmentInstructionsDisplayData(section)
        ? section.subtotal
        : showZrefPrices
          ? section.section.zrefSubTotal
          : section.section.subtotal;
      const table = isAttachmentInstructionsDisplayData(section)
        ? new ProductTable(
            pdf,
            this.localeId,
            `${group.label} - ${section.label}`,
            this.createAttachmentInstructionRows(section.attachmentInstructions, showZrefPrices),
            showCosts,
            subtotal,
          )
        : new ProductTable(
            pdf,
            this.localeId,
            `${group.label} - ${section.label}`,
            this.createFgdsProductListRows(section.section, showZrefPrices),
            showCosts,
            subtotal,
          );
      this.drawProductTable(
        pdf,
        container,
        tableOfContents,
        table,
        showCosts,
        isProductListOverall ? undefined : group.source.floorplanName,
      );
    });
  }

  private addAdditionalMonitoringProductTables(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    showCosts: boolean,
    additionalCategories: ReturnType<typeof buildMonitoringAdditionalCategoryDisplayData>,
  ) {
    additionalCategories.forEach((category) => {
      const table = new ProductTable(
        pdf,
        this.localeId,
        category.label,
        this.createAdditionalMonitoringProductListRows(category),
        showCosts,
      );
      this.drawProductTable(pdf, container, tableOfContents, table, showCosts);
    });
  }

  private addAdditionalFgdsProductTables(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    showCosts: boolean,
    additionalCategories: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>,
    showZrefPrices = false,
  ) {
    additionalCategories.forEach((category) => {
      const subtotal = showZrefPrices ? (category.zrefSubTotal ?? 0) : category.subtotal;
      const table = new ProductTable(
        pdf,
        this.localeId,
        category.label,
        this.createAdditionalFgdsProductListRows(category, showZrefPrices),
        showCosts,
        subtotal,
      );
      this.drawProductTable(pdf, container, tableOfContents, table, showCosts);
    });
  }

  private addServicesTable(
    project: FgdsProject,
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    headline: string,
    latestProductPositionId: number,
  ): number {
    const requiredServices = getRequiredFgdsProjectServicesWithPositions(project, latestProductPositionId).map(
      ({ definition, service, position }) => ({
        position,
        notes: service.notes,
        id: definition.partNumber,
        title: definition.label(),
        costs: service.costs,
      }),
    );
    if (!requiredServices.length) {
      return 0;
    }
    const servicesTableData: ServicesTableRow[] = this.generateServicesTableRows(requiredServices);
    const totalServicesCost = servicesTableData.reduce((acc, row) => acc + row.costs, 0);
    this.drawServicesTable(
      pdf,
      container,
      tableOfContents,
      headline,
      totalServicesCost,
      new ServicesTable(pdf, this.localeId, servicesTableData, this.productService.getFilterCountry()!),
      project.showCostFlag,
      true,
    );
    return totalServicesCost;
  }

  private drawServicesTable(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    headline: string,
    totalServicesCost: number,
    servicesTable: ServicesTable,
    showCosts: boolean,
    isRootSection = false,
  ) {
    const localCurrency = this.countryMapping.getCurrency(this.productService.getFilterCountry()!);
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    const servicesTableHeight: number = servicesTable.rows.map((row) => row.textHeight).reduce((acc, cur) => acc + cur, 0);
    const isEnoughSpace = this.checkIfIsEnoughSpace(
      servicesTableHeight +
        TableProperties.ROW_PADDING_Y +
        TableProperties.EMPTY_TABLE_HEIGHT +
        ProductListProperties.FULL_SUMMARY_HEIGHT +
        ProductListProperties.FOOTER_HEIGHT,
      container,
    );
    if (!this.isFirstContainerDrawn) {
      this.containerService.addOnNewPage(pdf, container, this.headlines.PRODUCT_LIST_OVERALL, tableOfContents);
      this.isFirstContainerDrawn = true;
    } else if (!isEnoughSpace) {
      this.addFooter(pdf, container);
      this.containerService.add(pdf, container, this.headlines.PRODUCT_LIST_OVERALL, tableOfContents);
    }
    this.addProductHeadline(pdf, container, headline, totalServicesCost, showCosts, isRootSection, localCurrency);
    this.addTableHeaders(pdf, container, servicesTable.columns);
    this.addRowSeparator(pdf, container, true);
    servicesTable.rows.forEach((row) => {
      const isEnoughSpace = this.checkIfIsEnoughSpace(row.textHeight + TableProperties.ROW_PADDING_Y, container);
      if (!isEnoughSpace) {
        this.addFooter(pdf, container);
        this.containerService.add(pdf, container, this.headlines.PRODUCT_LIST_OVERALL, tableOfContents);
        this.addProductHeadline(pdf, container, headline, totalServicesCost, showCosts, isRootSection, localCurrency);
        this.addTableHeaders(pdf, container, servicesTable.columns);
        this.addRowSeparator(pdf, container, true);
      }
      this.addServiceRow(pdf, container, servicesTable.columns, row, showCosts);
      this.addRowSeparator(pdf, container);
    });
    container.yPosition += TableProperties.GAP_BETWEEN_TABLES;
  }

  private addRowSeparator(pdf: jsPDF, container: HalfPageContainer, strong = false) {
    if (!strong) {
      pdf.setDrawColor(TableProperties.SEPARATOR_COLOR);
    } else {
      pdf.setDrawColor(TableProperties.SEPARATOR_COLOR_STRONG);
    }
    pdf.line(
      container.xPosition + TableProperties.PADDING_X,
      container.yPosition,
      container.xPosition + TableProperties.PADDING_X + TableProperties.ROW_SEPARATOR_LENGTH,
      container.yPosition,
    );
  }

  private addProductHeadline(
    pdf: jsPDF,
    container: HalfPageContainer,
    text: string,
    totalProductCost: number | undefined,
    showCosts: boolean,
    isRootSection = false,
    currency?: string,
  ) {
    pdf.setFont(FontNames.DRAEGER_PANGEA);
    pdf.setFontSize(isRootSection ? ProductListProperties.ROOT_SECTION_FONT_SIZE : TableProperties.HEADER_FONT_SIZE);
    container.yPosition += TableProperties.HEADLINE_OFFSET_Y;
    pdf.text(text, container.xPosition + 36, container.yPosition, { baseline: "top" });
    if (showCosts) {
      this.drawTotalProductCosts(pdf, container, totalProductCost, currency);
    }
    pdf.setFont(FontNames.DRAEGER_PANGEA_TEXT);
    container.yPosition += TableProperties.GAP_HEADLINE_HEADERS;
  }

  private drawStandaloneSectionHeadline(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    text: string,
    subtotal: number | undefined,
    showCosts: boolean,
    floorplanName?: string,
    isRootSection = false,
  ) {
    const pageHeadline = this.buildPageHeadline(pdf, floorplanName);
    if (!this.isFirstContainerDrawn) {
      this.containerService.addOnNewPage(pdf, container, pageHeadline, tableOfContents);
      this.isFirstContainerDrawn = true;
    } else if (!this.checkIfIsEnoughSpace(TableProperties.HEADER_HEIGHT, container)) {
      this.addFooter(pdf, container);
      this.containerService.add(pdf, container, pageHeadline, tableOfContents);
    }

    this.addProductHeadline(pdf, container, text, subtotal, showCosts, isRootSection);
    container.yPosition -= TableProperties.GAP_HEADLINE_HEADERS - TableProperties.PADDING_32;
  }

  private createPlaceholderProductTableRow(placeholderProduct: PlaceholderProduct, placements: string): ProductTableRow {
    const discount = placeholderProduct.discount ?? 0;
    const priceData = this.currentShowZrefPrices ? undefined : placeholderProduct.price;
    return {
      position: placeholderProduct.positionId,
      quantity: placements,
      name: placeholderProduct.name,
      id: placeholderProduct.id,
      priceData,
      discount,
      totalCosts: priceData ? calculateDiscountedPrice(priceData.price, discount) * Number(placements) : 0,
      showWarning: false,
      isPlaceholder: false,
    };
  }

  private createCustomProductTableRow(product: PlaceholderProduct, placements: string): ProductTableRow {
    const discount = product.discount ?? 0;
    const priceData = getProductListPriceData(product, this.currentShowZrefPrices);
    return {
      position: product.positionId,
      quantity: placements,
      name: product.name || $localize`:@@customPartNumber.placeholderName:Benutzerdefinierte Sachnummer`,
      id: product.id,
      priceData,
      discount,
      totalCosts: priceData ? calculateDiscountedPrice(priceData.price, discount) * Number(placements) : 0,
      showWarning: false,
      isPlaceholder: false,
    };
  }

  private createAttachmentInstructionRows(attachmentInstructions: ProductData[], showZrefPrices = false) {
    const rows: ProductTableRow[] = [];
    for (const instruction of attachmentInstructions) {
      const priceData = getProductListPriceData(instruction, showZrefPrices);
      const discount = instruction.discount ?? 0;
      const totalCost = priceData ? calculateDiscountedPrice(priceData.price, discount) : 0;
      rows.push(
        new ProductTableRow(
          instruction.positionId!,
          "1",
          instruction.name,
          instruction.id!,
          !instruction.isAvailable || instruction.isIncompleteConfiguration,
          false,
          totalCost,
          priceData,
          discount,
        ),
      );
    }
    return rows;
  }

  private createAdditionalMonitoringProductListRows(
    category: ReturnType<typeof buildMonitoringAdditionalCategoryDisplayData>[number],
  ): ProductTableRow[] {
    return category.configs.flatMap((config) => [
      new ProductTableRow(
        config.positionNumber ?? "",
        config.savedQuantity.toString(),
        config.name,
        config.mainProductId ?? "--",
        config.showWarning,
        false,
      ),
      ...config.subproducts.map(
        (product) =>
          new ProductTableRow(
            product.positionId ?? "",
            (config.savedQuantity * product.quantity).toString(),
            product.name,
            product.matNr,
            !product.isAvailable,
            false,
          ),
      ),
    ]);
  }

  private createAdditionalFgdsProductListRows(
    category: ReturnType<typeof buildFgdsAdditionalCategoryDisplayData>[number],
    showZrefPrices = false,
  ): ProductTableRow[] {
    const currencyCode = showZrefPrices ? "EUR" : this.countryMapping.getCurrency(this.productService.getFilterCountry()!);
    return category.configs.flatMap((config) => {
      const unitPrice = showZrefPrices ? (config.zrefUnitPrice ?? 0) : (config.unitPrice ?? 0);
      const total = showZrefPrices ? (config.zrefTotal ?? 0) : (config.total ?? 0);
      return [
        new ProductTableRow(
          config.positionNumber ?? "",
          config.savedQuantity.toString(),
          config.name,
          config.mainProductId ?? "--",
          config.showWarning,
          false,
          total,
          new ProductPriceData("calculated price", unitPrice, currencyCode),
          config.mainProduct?.discount ?? 0,
        ),
        ...config.subproducts.map((product) => {
          const priceData = getProductListPriceData(product, showZrefPrices);
          const productDiscount = product.discount ?? 0;
          const productTotal = priceData
            ? calculateDiscountedPrice(priceData.price, productDiscount) * config.savedQuantity * product.quantity
            : 0;
          return new ProductTableRow(
            product.positionId ?? "",
            (config.savedQuantity * product.quantity).toString(),
            product.name,
            product.id ?? "--",
            !product.isAvailable || product.isIncompleteConfiguration,
            false,
            productTotal,
            priceData,
            productDiscount,
          );
        }),
      ];
    });
  }

  private createMonitoringProductListRows(categorySection: MonitoringSectionDisplayData): ProductTableRow[] {
    const rows: ProductTableRow[] = [];
    for (const mapping of categorySection.mappings) {
      for (const data of mapping.data) {
        const quantity = mapping.placedProductsCount * data.quantity;
        rows.push(new ProductTableRow(data.positionId!, quantity.toString(), data.name, data.matNr, !data.isAvailable, false));
      }
      categorySection.devices.forEach((device) => {
        if (device.products) {
          device.products.forEach((product) => {
            if (product.id && this.monitoringProductBelongsToConfig(device, mapping)) {
              rows.push(this.createCustomProductTableRow(product, "1"));
            }
          });
        }
      });
    }
    categorySection.placeholders.forEach((placeholder) => {
      placeholder.products.forEach((product: PlaceholderProduct) => {
        if (product.id) rows.push(this.createPlaceholderProductTableRow(product, "1"));
      });
    });
    if (categorySection.placeholders.length > 0) {
      rows.push({
        position: this.getPositionIdForPlaceholders(categorySection.placeholders, categorySection.placeholderPositionPrefix),
        quantity: this.productListService.countUnspecifiedPlaceholders(categorySection.placeholders).toString(),
        name: $localize`:@@monitoring.productList.placeholder.description:Platzhalter (noch zu konfigurieren)`,
        id: "--",
        priceData: undefined,
        totalCosts: 0,
        showWarning: false,
        isPlaceholder: true,
      });
    }
    return rows;
  }

  private createFgdsProductListRows(productSection: FgdsSectionDisplayData, showZrefPrices = false): ProductTableRow[] {
    const rows: ProductTableRow[] = [];
    productSection.mappings.forEach((mapping) => {
      mapping.data.forEach((productData) => {
        const quantity = mapping.placedProductsCount * productData.quantity;
        const priceData = getProductListPriceData(productData, showZrefPrices);
        rows.push(
          new ProductTableRow(
            productData.positionId!,
            quantity.toString(),
            productData.name,
            productData.id!,
            !productData.isAvailable || productData.isIncompleteConfiguration,
            false,
            calculateDiscountedPrice(priceData?.price ?? 0, productData.discount ?? 0) * quantity,
            priceData,
            productData.discount ?? 0,
          ),
        );
      });
      productSection.devices.forEach((device) => {
        if (device.products) {
          device.products.forEach((product) => {
            if (product.id && this.fgdsProductBelongsToConfig(device, mapping)) {
              rows.push(this.createCustomProductTableRow(product, "1"));
            }
          });
        }
      });
    });

    productSection.placeholders.forEach((placeholder) => {
      placeholder.products.forEach((product: PlaceholderProduct) => {
        if (product.id) rows.push(this.createPlaceholderProductTableRow(product, "1"));
      });
    });
    if (productSection.placeholders.length > 0) {
      rows.push({
        position: this.getPositionIdForPlaceholders(productSection.placeholders, productSection.placeholderPositionPrefix),
        quantity: this.productListService.countUnspecifiedPlaceholders(productSection.placeholders).toString(),
        name: $localize`:@@productList.placeholder.description:Platzhalter (noch zu konfigurieren)`,
        id: "--",
        priceData: undefined,
        totalCosts: 0,
        showWarning: false,
        isPlaceholder: true,
      });
    }
    return rows;
  }

  private monitoringProductBelongsToConfig(
    product:
      FloorplanPatientMonitor | FloorplanCentralStation | FloorplanTelemetry | FloorplanKvm | FloorplanDisplay | FloorplanUtility,
    mapping: MonitoringConfigProductMap,
  ) {
    return product.config.id === mapping.config.id;
  }

  private fgdsProductBelongsToConfig(
    product:
      | FloorplanGasWarningCenter
      | FloorplanAlarmDevice
      | FloorplanSignalElement
      | FloorplanPlasticSign
      | FloorplanTransmitter
      | FloorplanFlameDetector,
    mapping: ConfigProductMap,
  ) {
    return product.config.id === mapping.config.id;
  }

  private getPositionIdForPlaceholders(placeholders: FloorplanPlaceholder[], placeholderPositionPrefix: string) {
    const placeHoldersWithNotes = placeholders.filter((placeholder) => placeholder.notes !== undefined);
    return placeHoldersWithNotes.length > 0 ? `${placeholderPositionPrefix}*` : `${placeholderPositionPrefix}`;
  }

  private generateServicesTableRows(servicesData: ServiceData[]): ServicesTableRow[] {
    return servicesData.map((serviceData: ServiceData) => {
      return {
        position: serviceData.position,
        title: serviceData.title,
        description: serviceData.notes,
        id: serviceData.id,
        costs: typeof serviceData.costs === "number" && !Number.isNaN(serviceData.costs) ? serviceData.costs : 0,
      };
    });
  }

  private addFooter(pdf: jsPDF, container: HalfPageContainer) {
    const footerY = Math.min(
      ContainerProperties.Y + ContainerProperties.HEIGHT - ProductListProperties.FOOTER_HEIGHT,
      container.yPosition,
    );
    pdf.setFontSize(18 * PdfProperties.POINT_TO_PIXEL_RATIO);
    pdf.text(
      $localize`:@@pdfExport.productList.asteriskExplanation:* Produktnotizen werden gesondert aufgeführt`,
      container.xPosition + TableProperties.PADDING_X,
      footerY + 32,
      { baseline: "top" },
    );

    pdf.setFillColor(PdfProperties.FOOTER_COLOR);
    pdf.setDrawColor("#ffffff");
    pdf.rect(container.xPosition + TableProperties.PADDING_X, footerY + 66, TableProperties.ROW_SEPARATOR_LENGTH, 44, "F");

    const icon = new Image();
    icon.src = "assets/odx_info.png";
    pdf.addImage(icon, "png", container.xPosition + TableProperties.PADDING_X + 10, footerY + 78, 20, 20);

    pdf.setFontSize(13 * PdfProperties.POINT_TO_PIXEL_RATIO);
    pdf.text(
      $localize`:@@productList.footer:Die Produktliste ist kein verbindliches Angebot`,
      container.xPosition + TableProperties.PADDING_X + 40,
      footerY + 82,
      { baseline: "top" },
    );
    container.yPosition = ContainerProperties.HEIGHT;
  }

  private drawProductListHint(pdf: jsPDF, container: HalfPageContainer) {
    pdf.setFillColor(PdfProperties.FOOTER_COLOR);
    pdf.setDrawColor("#ffffff");
    pdf.rect(
      container.xPosition + 0.5 * ContainerProperties.WIDTH - 10,
      container.yPosition + 66,
      0.5 * ContainerProperties.WIDTH - 16,
      44,
      "F",
    );

    const icon = new Image();
    icon.src = "assets/odx_info.png";
    pdf.addImage(icon, "png", container.xPosition + 0.5 * ContainerProperties.WIDTH, container.yPosition + 78, 20, 20);

    pdf.setFontSize(13 * PdfProperties.POINT_TO_PIXEL_RATIO);
    pdf.text(
      $localize`:@@productList.footer:Die Produktliste ist kein verbindliches Angebot`,
      container.xPosition + 0.5 * ContainerProperties.WIDTH + 30,
      container.yPosition + 82,
      { baseline: "top" },
    );
  }

  private drawProductTable(
    pdf: jsPDF,
    container: HalfPageContainer,
    tableOfContents: TableOfContents,
    table: ProductTable,
    showCosts: boolean,
    floorplanName?: string,
    isRootSection = false,
  ) {
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    const isEnoughSpace = this.checkIfIsEnoughSpace(
      table.rows[0].textHeight + TableProperties.ROW_PADDING_Y + TableProperties.EMPTY_TABLE_HEIGHT,
      container,
    );
    const pageHeadline = this.buildPageHeadline(pdf, floorplanName);
    if (!this.isFirstContainerDrawn) {
      this.containerService.addOnNewPage(pdf, container, pageHeadline, tableOfContents);
      this.isFirstContainerDrawn = true;
    } else if (!isEnoughSpace) {
      this.addFooter(pdf, container);
      this.containerService.add(pdf, container, pageHeadline, tableOfContents);
    }
    this.addProductHeadline(pdf, container, table.headline, table.subTotal, showCosts, isRootSection);
    this.addTableHeaders(pdf, container, table.columns);
    this.addRowSeparator(pdf, container, true);
    table.rows.forEach((row) => {
      const isEnoughSpace = this.checkIfIsEnoughSpace(row.textHeight + TableProperties.ROW_PADDING_Y, container);
      if (!isEnoughSpace) {
        this.addFooter(pdf, container);
        this.containerService.add(pdf, container, pageHeadline, tableOfContents);
        this.addProductHeadline(pdf, container, table.headline, table.subTotal, showCosts, isRootSection);
        this.addTableHeaders(pdf, container, table.columns);
        this.addRowSeparator(pdf, container, true);
      }
      this.addProductRow(pdf, container, table.columns, row, showCosts);
      this.addRowSeparator(pdf, container);
    });
    container.yPosition += TableProperties.GAP_BETWEEN_TABLES;
  }

  private buildPageHeadline(pdf: jsPDF, floorplanName?: string): string {
    if (!floorplanName) {
      return this.headlines.PRODUCT_LIST_OVERALL;
    }
    const prefix = `${this.headlines.PRODUCT_LIST} | `;
    pdf.setFontSize(PdfProperties.HEADLINE_FONT_SIZE);
    const truncatedName = truncateTextToWidth(pdf, floorplanName, MAX_HEADLINE_WIDTH - pdf.getTextWidth(prefix));
    return `${prefix}${truncatedName}`;
  }

  private checkIfIsEnoughSpace(height: number, container: HalfPageContainer) {
    return (
      ContainerProperties.Y + ContainerProperties.HEIGHT - ProductListProperties.FOOTER_HEIGHT - container.yPosition > height
    );
  }

  private buildRootAttachmentInstructions(groupSections: GroupSection[], additionalSections: AdditionalProductConfigSection[]) {
    const rootAttachmentInstructions = new Map<string, ProductData>();

    [
      ...this.productListService.getAttachmentInstructionsForProject(groupSections),
      ...this.productListService.getAttachmentInstructionsForAdditionalSections(additionalSections),
    ]
      .filter((instruction) => instruction.id)
      .forEach((instruction) => {
        const existingInstruction = rootAttachmentInstructions.get(instruction.id!);
        if (!existingInstruction) {
          rootAttachmentInstructions.set(instruction.id!, instruction);
          return;
        }

        if ((instruction.positionId ?? "").localeCompare(existingInstruction.positionId ?? "") < 0) {
          rootAttachmentInstructions.set(instruction.id!, instruction);
        }
      });

    return Array.from(rootAttachmentInstructions.values()).sort((first, second) =>
      comparePositionThenName(first.positionId, first.name, second.positionId, second.name),
    );
  }

  private drawSummary(pdf: jsPDF, container: HalfPageContainer, servicesCost: number, overallProductCost: number) {
    const localCurrency = this.countryMapping.getCurrency(this.productService.getFilterCountry()!);
    container.yPosition += 64;
    pdf.setFontSize(ProductListProperties.SUMMARY_FONT_SIZE);
    this.drawSummaryRow(pdf, container, overallProductCost, $localize`:@@global.products:Produkte`, this.getEffectiveCurrency());
    this.drawSummaryRow(pdf, container, servicesCost, $localize`:@@global.services:Dienstleistungen`, localCurrency);
    if (this.areCurrenciesMixed()) {
      this.drawSummaryHintRow(pdf, container, $localize`:@@productList.netSum:Nettosumme`, "--");
    } else {
      this.drawSummaryRow(
        pdf,
        container,
        overallProductCost + servicesCost,
        $localize`:@@productList.netSum:Nettosumme`,
        this.getEffectiveCurrency(),
      );
    }
  }

  private drawFloorplanSummary(pdf: jsPDF, container: HalfPageContainer, overallProductCost: number) {
    container.yPosition += 64;
    pdf.setFontSize(ProductListProperties.SUMMARY_FONT_SIZE);
    this.drawSummaryRow(
      pdf,
      container,
      overallProductCost,
      $localize`:@@productList.netSum:Nettosumme`,
      this.getEffectiveCurrency(),
    );
  }

  private drawSummaryFooter(pdf: jsPDF, container: HalfPageContainer) {
    pdf.setFontSize(18 * PdfProperties.POINT_TO_PIXEL_RATIO);
    this.drawProductListHint(pdf, container);
  }

  private drawFloorplanSummaryFooter(pdf: jsPDF, container: HalfPageContainer) {
    pdf.setFontSize(18 * PdfProperties.POINT_TO_PIXEL_RATIO);
    pdf.text(
      $localize`:@@pdfExport.productList.asteriskExplanation:* Produktnotizen werden gesondert aufgeführt`,
      container.xPosition + 0.5 * ContainerProperties.WIDTH - 10,
      container.yPosition + 32,
      { baseline: "top" },
    );
    this.drawProductListHint(pdf, container);
  }

  private drawSummaryRow(pdf: jsPDF, container: HalfPageContainer, overallProductCost: number, label: string, currency: string) {
    pdf.text(label, container.xPosition + 0.5 * ContainerProperties.WIDTH, container.yPosition, { baseline: "top" });
    pdf.text(
      this.currencyPipe.transform(overallProductCost, currency, "symbol", undefined, this.localeId) ?? "--",
      container.xPosition + ContainerProperties.WIDTH - 36,
      container.yPosition,
      {
        align: "right",
        baseline: "top",
      },
    );
    container.yPosition += 32;
  }

  private drawSummaryHintRow(pdf: jsPDF, container: HalfPageContainer, label: string, hintText: string) {
    pdf.text(label, container.xPosition + 0.5 * ContainerProperties.WIDTH, container.yPosition, { baseline: "top" });
    pdf.text(hintText, container.xPosition + ContainerProperties.WIDTH - 36, container.yPosition, {
      align: "right",
      baseline: "top",
    });
    container.yPosition += 32;
  }

  private getEffectiveCurrency(): string {
    if (this.currentShowZrefPrices) return ProductListPdfService.ZREF_CURRENCY;
    return this.countryMapping.getCurrency(this.productService.getFilterCountry()!);
  }

  private areCurrenciesMixed(): boolean {
    if (!this.currentShowZrefPrices) return false;
    const localCurrency = this.countryMapping.getCurrency(this.productService.getFilterCountry()!);
    return localCurrency !== ProductListPdfService.ZREF_CURRENCY;
  }

  private drawTotalProductCosts(pdf: jsPDF, container: HalfPageContainer, costs?: number, currency?: string) {
    pdf.text(
      this.currencyPipe.transform(costs, currency ?? this.getEffectiveCurrency(), "symbol", undefined, this.localeId) ?? "--",
      container.xPosition + ContainerProperties.WIDTH - 36,
      container.yPosition,
      {
        baseline: "top",
        align: "right",
      },
    );
  }

  private addTableHeaders(pdf: jsPDF, container: HalfPageContainer, columns: TableColumn[], showCosts = true) {
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    columns.forEach((column) => {
      if (!showCosts && column.isPriceColumn) {
        return;
      }
      if (column.isPriceColumn) {
        pdf.text(column.title, container.xPosition + column.xPosition, container.yPosition, {
          baseline: "top",
          align: "right",
        });
      } else {
        pdf.text(column.title, container.xPosition + column.xPosition, container.yPosition, { baseline: "top" });
      }
    });
    container.yPosition += TableProperties.TEXT_HEIGHT;
    container.yPosition += TableProperties.ROW_PADDING_BOTTOM;
  }

  private addProductRow(
    pdf: jsPDF,
    container: HalfPageContainer,
    columns: TableColumn[],
    row: ProductListProductRow | ProductListPlaceholderRow,
    showCosts: boolean,
  ) {
    container.yPosition += TableProperties.ROW_PADDING_TOP;
    if (row.isPlaceholder) {
      pdf.setTextColor(PdfProperties.ODX_RED);
    }
    if (row.showWarning) {
      pdf.setTextColor(PdfProperties.ODX_YELLOW);
      pdf.addImage(
        ImagePaths.WARNING_SIGN,
        "PNG",
        container.xPosition +
          columns[0].xPosition +
          pdf.getTextDimensions(row.position).w +
          0.5 * TableProperties.WARNING_ICON_SIZE,
        container.yPosition,
        TableProperties.WARNING_ICON_SIZE,
        TableProperties.WARNING_ICON_SIZE,
      );
    }
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    pdf.text(row.position, container.xPosition + columns[0].xPosition, container.yPosition, { baseline: "top" });
    pdf.text(row.quantity, container.xPosition + columns[1].xPosition, container.yPosition, { baseline: "top" });
    row.name.forEach((line, index) => {
      pdf.text(
        line,
        container.xPosition + columns[2].xPosition,
        container.yPosition + index * pdf.getTextDimensions(line).h * PdfProperties.POINT_TO_PIXEL_RATIO,
        { baseline: "top" },
      );
    });
    pdf.text(row.id, container.xPosition + columns[3].xPosition, container.yPosition, { baseline: "top" });
    if (showCosts) {
      pdf.text(row.priceData, container.xPosition + columns[4].xPosition, container.yPosition, {
        baseline: "top",
        align: "right",
      });
      pdf.text(row.discount, container.xPosition + columns[5].xPosition, container.yPosition, {
        baseline: "top",
        align: "right",
      });
      pdf.text(row.totalCosts, container.xPosition + columns[6].xPosition, container.yPosition, {
        baseline: "top",
        align: "right",
      });
    }
    pdf.setTextColor(PdfProperties.DRAEGERBLUE);
    container.yPosition += row.textHeight;
    container.yPosition += TableProperties.ROW_PADDING_BOTTOM;
  }

  private addServiceRow(
    pdf: jsPDF,
    container: HalfPageContainer,
    columns: TableColumn[],
    row: ProductListServiceRow,
    showCosts: boolean,
  ) {
    container.yPosition += TableProperties.ROW_PADDING_TOP;
    pdf.setFontSize(TableProperties.TEXT_FONT_SIZE);
    pdf.text(row.position, container.xPosition + columns[0].xPosition, container.yPosition, { baseline: "top" });
    pdf.text(row.title, container.xPosition + columns[1].xPosition, container.yPosition, { baseline: "top" });
    row.description.forEach((line, index) => {
      pdf.text(
        line,
        container.xPosition + columns[2].xPosition,
        container.yPosition + index * pdf.getTextDimensions(line).h * PdfProperties.POINT_TO_PIXEL_RATIO,
        { baseline: "top" },
      );
    });
    pdf.text(row.id, container.xPosition + columns[3].xPosition, container.yPosition, { baseline: "top" });
    if (showCosts) {
      pdf.text(row.costs, container.xPosition + columns[4].xPosition, container.yPosition, {
        baseline: "top",
        align: "right",
      });
    }
    pdf.setTextColor(PdfProperties.DRAEGERBLUE);
    container.yPosition += row.textHeight;
    container.yPosition += TableProperties.ROW_PADDING_BOTTOM;
  }
}
